import getSymbolFromCurrency from "currency-symbol-map";

// Explicit symbol overrides for currencies used by our supported countries.
// This guarantees a symbol-like display even if Intl falls back to ISO codes.
const SYMBOL_OVERRIDES = {
  USD: "$",
  CAD: "C$",
  GBP: "\u00A3",
  AUD: "A$",
  NZD: "NZ$",
  SGD: "S$",
  HKD: "HK$",
  MXN: "MX$",
  ARS: "AR$",
  CLP: "CL$",
  COP: "COL$",
  TWD: "NT$",
  XCD: "EC$",

  EUR: "\u20AC",
  PLN: "z\u0142",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  CHF: "CHF",

  CZK: "K\u010D",
  HUF: "Ft",
  RON: "lei",
  BGN: "\u043B\u0432",

  AED: "\u062F.\u0625",
  ILS: "\u20AA",
  TRY: "\u20BA",

  BRL: "R$",
  ZAR: "R",

  BDT: "\u09F3",
  XCG: "Cg",
  ZWG: "ZiG",
};

export function getCurrencySymbol(code) {
  const cur = String(code || "").toUpperCase();
  if (!cur) return null;

  // Prefer explicit overrides for currencies where Intl is inconsistent across locales.
  if (SYMBOL_OVERRIDES[cur]) return SYMBOL_OVERRIDES[cur];

  try {
    const mapped = String(getSymbolFromCurrency(cur) || "").trim();
    if (mapped && mapped.toUpperCase() !== cur) return mapped;
  } catch {
    // Fall through to Intl.
  }

  // Best-effort: ask Intl for a narrow symbol.
  // Note: Some currencies may still return the ISO code depending on locale support.
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: cur,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).formatToParts(0);

    const currencyPart = parts.find((p) => p.type === "currency");
    const sym = currencyPart?.value ? String(currencyPart.value).trim() : "";
    if (!sym) return null;

    // If Intl just echoes the code back, treat it as missing symbol.
    if (sym.toUpperCase() === cur) return null;

    return sym;
  } catch {
    return null;
  }
}

export function normalizeCurrencyRates(currencyPayload) {
  const rates = currencyPayload?.rates;
  if (!rates || typeof rates !== "object") return {};

  const direct = {};
  for (const [k, v] of Object.entries(rates)) {
    if (v && typeof v === "object" && typeof v.currency === "string" && v.rate != null) {
      const r = Number(v.rate);
      const currency = String(v.currency || "").trim().toUpperCase();
      if (Number.isFinite(r) && r > 0 && /^[A-Z]{3}$/.test(currency)) {
        direct[currency] = r;
      }
      continue;
    }

    const asNum = Number(v);
    const currency = String(k || "").trim().toUpperCase();
    if (Number.isFinite(asNum) && asNum > 0 && /^[A-Z]{3}$/.test(currency)) {
      direct[currency] = asNum;
    }
  }
  direct.USD = 1;
  return direct;
}

export function convertAmount(amount, { from = "USD", to = "USD", rates = {} } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  if (from === to) return n;

  const fromRate = from === "USD" ? 1 : Number(rates[from]);
  const toRate = to === "USD" ? 1 : Number(rates[to]);

  // Rates are defined as: 1 USD = rate[currency]
  if (from !== "USD" && !(Number.isFinite(fromRate) && fromRate > 0)) return n;
  if (to !== "USD" && !(Number.isFinite(toRate) && toRate > 0)) return n;

  if (from === "USD") return n * toRate;
  if (to === "USD") return n / fromRate;
  return n * (toRate / fromRate);
}

export function formatMoney(amount, {
  to = "USD",
  from = "USD",
  rates = {},
  showCodeForNonSymbol = true,
} = {}) {
  const n = Number(amount);
  const safeInput = Number.isFinite(n) ? n : 0;

  const fmt = (val, code) => {
    const sym = getCurrencySymbol(code);
    const rounded = (Number.isFinite(val) ? val : 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (sym) {
      // If the symbol is multi-letter (e.g., CHF, lei), add a space for readability.
      const needsSpace = /^[A-Za-z]{2,}$/.test(sym);
      return needsSpace ? `${sym} ${rounded}` : `${sym}${rounded}`;
    }
    return showCodeForNonSymbol ? `${code} ${rounded}` : rounded;
  };

  // If we can't actually convert (missing rates), don't lie with the target currency.
  if (from !== to) {
    const fromRate = from === "USD" ? 1 : Number(rates[from]);
    const toRate = to === "USD" ? 1 : Number(rates[to]);
    const canConvertFrom = from === "USD" || (Number.isFinite(fromRate) && fromRate > 0);
    const canConvertTo = to === "USD" || (Number.isFinite(toRate) && toRate > 0);
    if (!canConvertFrom || !canConvertTo) {
      return fmt(safeInput, from);
    }
  }

  const converted = convertAmount(safeInput, { from, to, rates });
  return fmt(converted, to);
}

export function formatWholeMoney(amount, {
  to = "USD",
  from = "USD",
  rates = {},
  showCodeForNonSymbol = true,
} = {}) {
  const n = Number(amount);
  const safeInput = Number.isFinite(n) ? n : 0;

  const fmt = (val, code) => {
    const sym = getCurrencySymbol(code);
    const whole = Number.isFinite(val) && val > 0 ? Math.ceil(val - Number.EPSILON) : 0;
    const rounded = whole.toLocaleString("en-US", { maximumFractionDigits: 0 });
    if (sym) {
      const needsSpace = /^[A-Za-z]{2,}$/.test(sym);
      return needsSpace ? `${sym} ${rounded}` : `${sym}${rounded}`;
    }
    return showCodeForNonSymbol ? `${code} ${rounded}` : rounded;
  };

  if (from !== to) {
    const fromRate = from === "USD" ? 1 : Number(rates[from]);
    const toRate = to === "USD" ? 1 : Number(rates[to]);
    const canConvertFrom = from === "USD" || (Number.isFinite(fromRate) && fromRate > 0);
    const canConvertTo = to === "USD" || (Number.isFinite(toRate) && toRate > 0);
    if (!canConvertFrom || !canConvertTo) {
      return fmt(safeInput, from);
    }
  }

  const converted = convertAmount(safeInput, { from, to, rates });
  return fmt(converted, to);
}

export function guessCurrencyFromText(text) {
  if (!text) return null;
  const str = String(text);

  // Match a 3-letter currency code if present.
  const codeMatch = str.match(/\b([A-Z]{3})\b/);
  if (codeMatch) return codeMatch[1];

  // Heuristics for common formats
  if (str.includes("US $") || str.includes("US$") || str.includes("USD")) return "USD";
  if (str.includes("\u09F3") || str.includes("BDT")) return "BDT";
  if (str.includes("\u20AC") || str.includes("EUR")) return "EUR";
  if (str.includes("\u00A3") || str.includes("GBP")) return "GBP";

  return null;
}

